import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar, Clock, Loader2, Moon, Sun, AlertTriangle, CheckCircle, Zap } from 'lucide-react';
import apiClient from '../../lib/apiClient';

/**
 * SchedulerToggle — Dual toggle for business hours and off-hours scraping
 * Shows throttle status and allows independent control of each mode
 */
function SchedulerToggle() {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);

  // Fetch scheduler status
  const { data: status, isLoading, error } = useQuery({
    queryKey: ['scheduler', 'status'],
    queryFn: () => apiClient.get('/api/coverage/scheduler/status'),
    refetchInterval: 15000,
  });

  // Toggle mutation — switches flip on click (optimistic, UI-responsiveness
  // review 2026-08-08); a failed write rolls back.
  const toggleMutation = useMutation({
    mutationFn: (updates) => apiClient.post('/api/coverage/scheduler/toggle', updates),
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: ['scheduler', 'status'] });
      const previous = queryClient.getQueryData(['scheduler', 'status']);
      queryClient.setQueryData(['scheduler', 'status'], (prev) => (
        prev ? { ...prev, config: { ...(prev.config || {}), ...updates } } : prev
      ));
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['scheduler', 'status'], ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduler', 'status'] });
    },
  });

  const handleToggle = (field) => {
    const currentValue = status?.config?.[field] ?? false;
    toggleMutation.mutate({ [field]: !currentValue });
  };

  if (isLoading) {
    return (
      <div className="sd-scheduler-toggle sd-scheduler-loading">
        <Loader2 size={14} className="sd-spin" />
        <span>Scheduler</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sd-scheduler-toggle sd-scheduler-error" title={error.message}>
        <Calendar size={14} />
        <span>Error</span>
      </div>
    );
  }

  const config = status?.config || {};
  const throttle = status?.throttle || {};
  const anyEnabled = config.businessHours || config.offHours;
  const isBlocked = throttle.blocked;

  // Calculate time until unblock
  let blockTimeRemaining = null;
  if (isBlocked && throttle.blockedUntil) {
    const remaining = new Date(throttle.blockedUntil) - new Date();
    if (remaining > 0) {
      blockTimeRemaining = Math.ceil(remaining / 60000);
    }
  }

  return (
    <div className={`sd-scheduler-toggle ${anyEnabled ? 'sd-scheduler-on' : 'sd-scheduler-off'} ${isBlocked ? 'sd-scheduler-blocked' : ''}`}>
      <button
        className="sd-scheduler-btn"
        onClick={() => setExpanded(!expanded)}
        title="Click to expand scheduler controls"
      >
        <span className="sd-scheduler-icon">
          {isBlocked ? (
            <AlertTriangle size={14} />
          ) : anyEnabled ? (
            <Zap size={14} />
          ) : (
            <Calendar size={14} />
          )}
        </span>
        <span className="sd-scheduler-label">
          Scraper
        </span>
        <span className={`sd-scheduler-state ${anyEnabled ? 'is-on' : 'is-off'} ${isBlocked ? 'is-blocked' : ''}`}>
          {isBlocked ? 'BLOCKED' : anyEnabled ? 'ON' : 'OFF'}
        </span>
      </button>

      {/* Expanded panel */}
      {expanded && (
        <div className="sd-scheduler-panel">
          {/* Throttle warning */}
          {isBlocked && (
            <div className="sd-scheduler-warning">
              <AlertTriangle size={12} />
              <span>
                Site blocked — {blockTimeRemaining ? `${blockTimeRemaining}min until retry` : 'probing...'}
              </span>
            </div>
          )}

          {/* Business Hours Toggle */}
          <div className="sd-scheduler-row">
            <div className="sd-scheduler-row-label">
              <Sun size={14} />
              <span>Business Hours</span>
              <span className="sd-scheduler-time">6AM-6PM PST</span>
            </div>
            <button
              className={`sd-scheduler-toggle-btn ${config.businessHours ? 'is-on' : 'is-off'}`}
              onClick={() => handleToggle('businessHours')}
            >
              {config.businessHours ? <CheckCircle size={12} /> : null}
              {config.businessHours ? 'ON' : 'OFF'}
            </button>
          </div>

          {/* Off-Hours Toggle */}
          <div className="sd-scheduler-row">
            <div className="sd-scheduler-row-label">
              <Moon size={14} />
              <span>Off-Hours</span>
              <span className="sd-scheduler-time">6PM-6AM + Weekends</span>
            </div>
            <button
              className={`sd-scheduler-toggle-btn ${config.offHours ? 'is-on' : 'is-off'}`}
              onClick={() => handleToggle('offHours')}
            >
              {config.offHours ? <CheckCircle size={12} /> : null}
              {config.offHours ? 'ON' : 'OFF'}
            </button>
          </div>

          {/* Status info */}
          <div className="sd-scheduler-status">
            {status?.isAllowedTime ? (
              <span className="sd-scheduler-active">
                <Zap size={10} /> Active now
              </span>
            ) : (
              <span className="sd-scheduler-waiting">
                <Clock size={10} /> Waiting for allowed hours
              </span>
            )}
            {throttle.consecutiveFailures > 0 && (
              <span className="sd-scheduler-failures">
                {throttle.consecutiveFailures} recent failures
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SchedulerToggle;
